import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, Plus, Pencil, Trash2 } from "lucide-react";
import { Card, Open } from "./AnalyticsParts";
import { formatDateTime } from "@/lib/datetime";

const FIELDS = [["manufacturer", "Manufacturer", "text"], ["serial_number", "Serial number", "text"], ["purchase_date", "Purchase date", "date"],
  ["installation_date", "Installation date", "date"], ["warranty_end", "Warranty ends", "date"], ["warranty_notes", "Warranty notes", "text"], ["notes", "Notes", "text"]];
const d = (v) => (v ? formatDateTime(v).split(",")[0] : "–");

function EquipForm({ customerId, item, jobs, onClose, onSaved }) {
  const [f, setF] = useState(() => item ? Object.fromEntries(FIELDS.map(([k]) => [k, (item[k] || "").slice(0, k.includes("date") || k === "warranty_end" ? 10 : 500)])) : {});
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => { if (!item) api.get("/equipment/catalogue", { params: { q } }).then((r) => setOpts(r.data)).catch(() => {}); }, [q, item]);
  const save = async () => {
    try {
      if (item) await api.patch(`/customers/${customerId}/equipment/${item.id}`, f);
      else await api.post(`/customers/${customerId}/equipment`, f);
      toast.success("Equipment saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const manualOnly = (k) => item && item.source !== "manual" && k === "purchase_date";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="equipment-form">
        <DialogHeader><DialogTitle>{item ? `Edit ${item.model}` : "Add equipment"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!item && <div>
            <Input placeholder="Search Hot Tubs and Swim Spas…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="equipment-catalogue-search" />
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md mt-1">
              {opts.map((p) => (
                <button key={p.legacy_id} type="button" onClick={() => setF({ ...f, product_legacy_id: p.legacy_id })} data-testid={`equipment-option-${p.legacy_id}`}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${f.product_legacy_id === p.legacy_id ? "bg-indigo-50 text-indigo-700" : ""}`}>{p.title} <span className="text-xs text-slate-400">· {p.category}</span></button>
              ))}
            </div>
          </div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.filter(([k]) => !manualOnly(k)).map(([k, l, t]) => (
              <label key={k} className="text-xs text-slate-500">{l}<Input type={t} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`equipment-field-${k}`} /></label>
            ))}
            {(!item || item.source === "manual") && <label className="text-xs text-slate-500 sm:col-span-2">Linked order / job (makes it an HTSS sale only if that job is a real sale)
              <select value={f.task_legacy_id || ""} onChange={(e) => setF({ ...f, task_legacy_id: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-200 text-sm px-2" data-testid="equipment-field-task">
                <option value="">None</option>{jobs.map((j) => <option key={j.legacy_id} value={j.legacy_id}>{`${j.legacy_id} · ${j.title || ""}`}</option>)}
              </select></label>}
          </div>
          <Button onClick={save} disabled={!item && !f.product_legacy_id} data-testid="equipment-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Links = ({ label, items, testid }) => !!items?.length && (
  <div className="text-xs" data-testid={testid}><span className="text-slate-400">{label}: </span>{items.map((j, i) => <span key={j.legacy_id}>{i ? ", " : ""}<Open to={j.link} /> <span className="text-slate-600">{j.legacy_id} {j.date && `(${j.date})`}</span></span>)}</div>
);

function EquipCard({ e, onEdit, onRemove }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 flex gap-3" data-testid={`equipment-card-${e.id}`}>
      <div className="w-20 h-20 shrink-0 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
        {e.image ? <img src={e.image} alt={e.model} className="w-full h-full object-cover" /> : <Package className="w-7 h-7 text-slate-300" />}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">{e.category}</div>
            <div className="text-sm font-bold text-slate-900 break-words" data-testid={`equipment-model-${e.id}`}>{e.model}</div>
            <div className="text-xs text-slate-500">{e.manufacturer || "Manufacturer not set"}</div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={onEdit} className="p-1 text-slate-400 hover:text-indigo-600" data-testid={`equipment-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={onRemove} className="p-1 text-slate-400 hover:text-rose-600" data-testid={`equipment-remove-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 text-xs text-slate-600">
          <span>Purchased: {d(e.purchase_date)}</span>
          <span>Installed: {d(e.installation_date)}{e.installation_source && e.installation_source !== "staff" ? ` (${e.installation_source})` : ""}</span>
          <span>Serial: {e.serial_number || "–"}</span>
          <span>Warranty: {e.warranty_end ? `until ${d(e.warranty_end)}` : "not recorded"}</span>
        </div>
        {e.warranty_notes && <div className="text-xs text-slate-500">{e.warranty_notes}</div>}
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className={`rounded px-1.5 py-0.5 font-semibold ${e.is_htss_sale ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`equipment-sale-${e.id}`}>{e.is_htss_sale ? "HTSS sale" : "Not an HTSS sale"}</span>
          <span className="text-slate-400">{e.source === "woocommerce" ? `Web order #${e.order_ref}` : e.source === "quote" ? `Accepted quote ${e.order_ref}` : e.source === "tedops_job" ? "From TedOps job" : "Added by staff"}</span>
          {e.order_link && <Open to={e.order_link} testid={`equipment-order-${e.id}`} />}
        </div>
        <Links label="Installations" items={e.installations} testid={`equipment-installs-${e.id}`} />
        <Links label="Service jobs" items={e.service_jobs} testid={`equipment-service-${e.id}`} />
        <Links label="Warranty claims" items={e.warranty_claims} testid={`equipment-warranty-${e.id}`} />
        {e.installation_media_count > 0 && <div className="text-xs text-slate-500">{e.installation_media_count} installation photo/video(s) on the Profile tab</div>}
      </div>
    </div>
  );
}

export function ProductsOwned({ customerId }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/customers/${customerId}/equipment`).then((r) => setData(r.data)).catch(() => setData({ enabled: false, items: [] }));
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data?.enabled) return null;
  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.model} from this customer's equipment?`)) return;
    try { await api.delete(`/customers/${customerId}/equipment/${e.id}`); load(); } catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };
  return (
    <Card title="Products owned" icon={Package} testid="analytics-products-owned"
      right={<Button size="sm" variant="outline" onClick={() => setEdit("new")} data-testid="equipment-add"><Plus className="w-4 h-4 mr-1" /> Add equipment</Button>}>
      <p className="text-[11px] text-slate-400 mb-3">{data.categories.join(", ")} only. Accessories and consumables stay in purchase history.</p>
      {!data.items.length ? <p className="text-sm text-slate-400" data-testid="equipment-empty">No major equipment recorded.</p> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{data.items.map((e) => <EquipCard key={e.id} e={e} onEdit={() => setEdit(e)} onRemove={() => remove(e)} />)}</div>
      )}
      {edit && <EquipForm customerId={customerId} item={edit === "new" ? null : edit} jobs={data.jobs} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Card>
  );
}
